
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { ClockIcon, DotsVerticalIcon, DownloadIcon, DuplicateIcon, FolderIcon, PencilIcon, PlusIcon, SearchIcon, SparklesIcon, StarIcon, TemplateIcon, TrashIcon, UploadIcon, UserGroupIcon, VideoCameraIcon, XIcon, } from '@heroicons/react/outline';
import { ShareIcon } from '@heroicons/react/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/solid';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import nProgress from 'nprogress';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Workflow } from 'types';
import clsxm from '@/lib/clsxm';
import { workflowTemplates } from '@/lib/workflows/templates';
import Button from '@/components/buttons/Button';
import DashboardLayout from '@/components/layout/DashboardLayout';
import Seo from '@/components/Seo';
import { useUser } from '@/utils/useUser';
export default function WorkflowsPage() {
    const router = useRouter();
    const { t } = useLocale('common');
    const { user, isTeamsPlan } = useUser();
    const [workflows, setWorkflows] = useState<Workflow[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'my-workflows' | 'shared' | 'templates'>('my-workflows');
    const [searchQuery, setSearchQuery] = useState('');
    const [starredWorkflows, setStarredWorkflows] = useState<Set<string>>(new Set());
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        fetchWorkflows();
    }, []);
    // If user has no workflows, default to showing Templates tab
    useEffect(() => {
        if (!loading && workflows.length === 0 && activeTab === 'my-workflows') {
            setActiveTab('templates');
        }
    }, [loading, workflows.length, activeTab]);
    const fetchWorkflows = async () => {
        try {
            const response = await fetch('/api/workflows');
            if (response.ok) {
                const data = await response.json();
                setWorkflows(data);
            }
        }
        catch (error) {
            console.error('Failed to fetch workflows:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleCreateWorkflow = async () => {
        try {
            const response = await fetch('/api/workflows', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: `${t('New Workflow')} ${new Date().toLocaleDateString()}`,
                    description: '',
                    category: 'custom',
                }),
            });
            if (response.ok) {
                const workflow = await response.json();
                router.push(`/workflows/${workflow.id}`);
            }
        }
        catch (error) {
            console.error('Failed to create workflow:', error);
            alert('Failed to create workflow. Please try again.');
        }
    };
    const handleDeleteWorkflow = async (id: string) => {
        if (!confirm(t('Are you sure you want to delete this workflow?')))
            return;
        try {
            const response = await fetch(`/api/workflows/${id}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                setWorkflows(workflows.filter((w) => w.id !== id));
            }
        }
        catch (error) {
            console.error('Failed to delete workflow:', error);
            alert('Failed to delete workflow. Please try again.');
        }
    };
    const handleDuplicateWorkflow = async (workflow: Workflow) => {
        try {
            const response = await fetch('/api/workflows', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: `${workflow.name} (Copy)`,
                    description: workflow.description,
                    category: workflow.category,
                    flow_data: workflow.flow_data,
                }),
            });
            if (response.ok) {
                const newWorkflow = await response.json();
                setWorkflows([newWorkflow, ...workflows]);
            }
        }
        catch (error) {
            console.error('Failed to duplicate workflow:', error);
            alert('Failed to duplicate workflow. Please try again.');
        }
    };
    const handleExportWorkflow = (workflow: Workflow) => {
        const exportData = {
            name: workflow.name,
            description: workflow.description || '',
            category: workflow.category || 'custom',
            flow_data: workflow.flow_data,
        };
        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${workflow.name.replace(/[^a-zA-Z0-9-_ ]/g, '')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };
    const handleImportWorkflow = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            if (!data.flow_data ||
                !Array.isArray(data.flow_data.nodes) ||
                !Array.isArray(data.flow_data.edges)) {
                alert(t('Invalid workflow file. The file must contain flow_data with nodes and edges.'));
                return;
            }
            const response = await fetch('/api/workflows', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: data.name || file.name.replace(/\.json$/, ''),
                    description: data.description || '',
                    category: data.category || 'custom',
                    flow_data: data.flow_data,
                }),
            });
            if (response.ok) {
                const newWorkflow = await response.json();
                router.push(`/workflows/${newWorkflow.id}`);
            }
            else {
                alert(t('Failed to import workflow. Please try again.'));
            }
        }
        catch {
            alert(t('Failed to read workflow file. Please make sure it is a valid JSON file.'));
        }
        finally {
            // Reset input so the same file can be imported again
            event.target.value = '';
        }
    };
    const handleToggleShareWithTeam = async (workflowId: string, currentValue: boolean) => {
        try {
            nProgress.start();
            const response = await fetch('/api/workflows/share', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    workflowId,
                    shareWithTeam: !currentValue,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to update sharing');
            }
            const data = await response.json();
            setWorkflows((prev) => prev.map((w) => w.id === workflowId
                ? {
                    ...w,
                    share_with_team: !currentValue,
                    team_id: data.workflow?.team_id,
                }
                : w));
            toast.success(data.message);
        }
        catch (error) {
            console.error('Failed to toggle share with team:', error);
            toast.error('Failed to update sharing settings');
        }
        finally {
            nProgress.done();
        }
    };
    const toggleStarWorkflow = (id: string) => {
        setStarredWorkflows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            }
            else {
                newSet.add(id);
            }
            return newSet;
        });
    };
    const ownedWorkflows = useMemo(() => workflows.filter((w) => w.user_id === user?.id), [workflows, user?.id]);
    const sharedWorkflowsList = useMemo(() => {
        const shared = workflows.filter((w) => w.user_id !== user?.id);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return shared.filter((w) => w.name.toLowerCase().includes(q) ||
                (w.description || '').toLowerCase().includes(q) ||
                w.category?.toLowerCase().includes(q));
        }
        return [...shared].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    }, [workflows, user?.id, searchQuery]);
    const filteredWorkflows = useMemo(() => {
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            return ownedWorkflows.filter((w) => w.name.toLowerCase().includes(q) ||
                (w.description || '').toLowerCase().includes(q) ||
                w.category?.toLowerCase().includes(q));
        }
        return [...ownedWorkflows].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    }, [ownedWorkflows, searchQuery]);
    const starredWorkflowsList = useMemo(() => {
        return filteredWorkflows.filter((w) => starredWorkflows.has(w.id));
    }, [filteredWorkflows, starredWorkflows]);
    const LoadingSkeleton = () => (<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
      {Array.from({ length: 6 }).map((_, idx) => (<div key={idx} className='group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-900/5 dark:ring-white/10'>
          <div className='aspect-[16/9] bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 animate-pulse'/>
          <div className='p-6 space-y-4'>
            <div className='h-6 bg-slate-200 dark:bg-slate-700 rounded-lg w-3/4 animate-pulse'/>
            <div className='h-4 bg-slate-100 dark:bg-slate-800 rounded w-full animate-pulse'/>
            <div className='h-4 bg-slate-100 dark:bg-slate-800 rounded w-5/6 animate-pulse'/>
            <div className='flex items-center gap-2 pt-2'>
              <div className='h-8 bg-slate-100 dark:bg-slate-800 rounded-full w-20 animate-pulse'/>
              <div className='h-8 bg-slate-100 dark:bg-slate-800 rounded-full w-24 animate-pulse'/>
            </div>
          </div>
        </div>))}
    </div>);
    const getWorkflowPreviewImage = (workflow: Workflow): string | null => {
        const nodes = workflow.flow_data?.nodes;
        if (!Array.isArray(nodes))
            return null;
        const videoExtensions = [
            '.mp4',
            '.webm',
            '.mov',
            '.avi',
            '.mkv',
            '.mp3',
            '.wav',
            '.ogg',
            '.aac',
        ];
        const isImageUrl = (url: string) => {
            if (!url.startsWith('http'))
                return false;
            const path = url.split('?')[0].toLowerCase();
            return !videoExtensions.some((ext) => path.endsWith(ext));
        };
        const imageNodeTypes = [
            'image-generation',
            'select-image',
            'image-edit',
            'image-upscaler',
            'image-splitter',
        ];
        // Prioritize image-only nodes first
        const sorted = [...nodes].sort((a, b) => {
            const aIsImage = imageNodeTypes.includes(a.type) ? 0 : 1;
            const bIsImage = imageNodeTypes.includes(b.type) ? 0 : 1;
            return aIsImage - bIsImage;
        });
        for (const node of sorted) {
            const result = node.data?.result;
            if (result) {
                const url = Array.isArray(result) ? result[0] : result;
                if (typeof url === 'string' && isImageUrl(url))
                    return url;
            }
            const configUrl = node.data?.config?.imageUrl || node.data?.config?.selectedImageUrl;
            if (typeof configUrl === 'string' && isImageUrl(configUrl))
                return configUrl;
        }
        return null;
    };
    const WorkflowCardMenu = ({ workflow, onClose, }: {
        workflow: Workflow;
        onClose: () => void;
    }) => {
        useEffect(() => {
            const handleClickOutside = () => onClose();
            document.addEventListener('click', handleClickOutside);
            return () => document.removeEventListener('click', handleClickOutside);
        }, [onClose]);
        return (<div className='absolute right-2 top-12 z-50 w-48 rounded-xl bg-white dark:bg-slate-800 shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden' onClick={(e) => e.stopPropagation()}>
        <div className='py-1'>
          <button onClick={(e) => {
                e.stopPropagation();
                router.push(`/workflows/${workflow.id}`);
                onClose();
            }} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
            <PencilIcon className='h-4 w-4'/>
            {t('Edit')}
          </button>
          <button onClick={(e) => {
                e.stopPropagation();
                handleDuplicateWorkflow(workflow);
                onClose();
            }} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
            <DuplicateIcon className='h-4 w-4'/>
            {t('Duplicate')}
          </button>
          <button onClick={(e) => {
                e.stopPropagation();
                handleExportWorkflow(workflow);
                onClose();
            }} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
            <DownloadIcon className='h-4 w-4'/>
            {t('Export')}
          </button>
          <button onClick={(e) => {
                e.stopPropagation();
                toggleStarWorkflow(workflow.id);
                onClose();
            }} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
            {starredWorkflows.has(workflow.id) ? (<StarIconSolid className='h-4 w-4 text-yellow-500'/>) : (<StarIcon className='h-4 w-4'/>)}
            {starredWorkflows.has(workflow.id)
                ? t('Unstar')
                : t('Add to favorites')}
          </button>
          {workflow.user_id === user?.id && (<>
              <div className='my-1 h-px bg-slate-200 dark:bg-slate-700'/>
              {isTeamsPlan ? (<button onClick={(e) => {
                        e.stopPropagation();
                        handleToggleShareWithTeam(workflow.id, Boolean(workflow.share_with_team));
                        onClose();
                    }} className='flex w-full items-center justify-between px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
                  <span className='flex items-center gap-3'>
                    <UserGroupIcon className='h-4 w-4'/>
                    {t('Share with Team')}
                  </span>
                  <span className={clsxm('ml-2 inline-block h-3 w-3 rounded-full', workflow.share_with_team
                        ? 'bg-green-500'
                        : 'bg-slate-300 dark:bg-slate-600')}/>
                </button>) : (<a href='/settings/billing-teams' target='_blank' onClick={(e) => e.stopPropagation()} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors'>
                  <UserGroupIcon className='h-4 w-4'/>
                  {t('Upgrade to Teams')}
                </a>)}
            </>)}
          <div className='my-1 h-px bg-slate-200 dark:bg-slate-700'/>
          <button onClick={(e) => {
                e.stopPropagation();
                handleDeleteWorkflow(workflow.id);
                onClose();
            }} className='flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors'>
            <TrashIcon className='h-4 w-4'/>
            {t('Delete')}
          </button>
        </div>
      </div>);
    };
    const WorkflowCard = ({ workflow, isStarred, }: {
        workflow: Workflow;
        isStarred: boolean;
    }) => (<div className='group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-900/5 dark:ring-white/10 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-900/10 hover:-translate-y-1 cursor-pointer dark:hover:shadow-black/30' onClick={() => router.push(`/workflows/${workflow.id}`)}>
      {/* Preview Area */}
      <div className='relative aspect-[16/9] overflow-hidden'>
        <img src={getWorkflowPreviewImage(workflow) ||
            'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/a2a6e227-9357-4b52-89f8-88d9d5f55868.png'} alt='Workflow preview' className='absolute inset-0 w-full h-full object-cover'/>
        <div className='absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-900/30 to-slate-900/40'/>
        <div className='absolute inset-0 flex items-center justify-center'></div>
        {/* Gradient overlay on hover */}
        <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'/>
      </div>

      {/* Content */}
      <div className='p-6'>
        <div className='flex items-start justify-between gap-3 mb-3'>
          <h3 className='font-bold text-lg text-slate-900 dark:text-slate-100 line-clamp-1 flex-1'>
            {workflow.name}
          </h3>
          <div className='flex items-center gap-1 flex-shrink-0'>
            <button onClick={(e) => {
            e.stopPropagation();
            toggleStarWorkflow(workflow.id);
        }} className='p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors' aria-label={isStarred ? t('Unstar') : t('Add to favorites')}>
              {isStarred ? (<StarIconSolid className='h-5 w-5 text-yellow-500'/>) : (<StarIcon className='h-5 w-5 text-slate-400 hover:text-yellow-500'/>)}
            </button>
            <button onClick={(e) => {
            e.stopPropagation();
            setOpenMenuId(openMenuId === workflow.id ? null : workflow.id);
        }} className='p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors' aria-label={t('More options')}>
              <DotsVerticalIcon className='h-5 w-5 text-slate-400'/>
            </button>
          </div>
        </div>

        {workflow.description && (<p className='text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-4'>
            {workflow.description}
          </p>)}

        <div className='flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800'>
          <div className='flex items-center gap-2'>
            {workflow.category && (<span className='inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-300'>
                {workflow.category}
              </span>)}
            {workflow.share_with_team && workflow.user_id === user?.id && (<span className='inline-flex items-center gap-1 rounded-lg bg-green-50 dark:bg-green-900/20 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-300 ring-1 ring-green-200/50 dark:ring-green-700/30'>
                <UserGroupIcon className='h-3 w-3'/>
                {t('Shared')}
              </span>)}
          </div>
          <div className='flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400'>
            <ClockIcon className='h-3.5 w-3.5'/>
            {new Date(workflow.updated_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Menu */}
      {openMenuId === workflow.id && (<WorkflowCardMenu workflow={workflow} onClose={() => setOpenMenuId(null)}/>)}
    </div>);
    return (<DashboardLayout>
      <Seo templateTitle={t('AI Workflows')}/>

      <main className='flex-1 overflow-y-auto focus:outline-none'>
        <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12'>
          {/* Hero */}
          <div className='relative overflow-hidden rounded-3xl border border-slate-200/60 dark:border-white/5 bg-slate-900'>
            {/* Background Image */}
            <div className='absolute inset-0'>
              <img src='https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/053913e8-902d-40d2-9942-1e1de07f6b8c.png' alt='Workflow Builder Background' className='w-full h-full object-cover opacity-90'/>
              <div className='absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-900/50 to-slate-900/60'/>
            </div>
            <div className='relative px-6 sm:px-10 py-10 sm:py-14'>
              <div className='max-w-3xl'>
                <div className='inline-flex items-center gap-2 rounded-full bg-primary-500/20 backdrop-blur-sm px-3 py-1.5 mb-6 ring-1 ring-primary-400/30'>
                  <SparklesIcon className='h-4 w-4 text-primary-300'/>
                  <span className='text-sm font-medium text-primary-200'>
                    {t('AI-Powered Automation')}
                  </span>
                </div>
                <h1 className='text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight'>
                  {t('AI Workflow Builder')}
                </h1>
                <p className='mt-6 text-lg sm:text-xl text-slate-200 leading-relaxed'>
                  {t('Design powerful, automated multi-step AI workflows for images, video, and more.')}
                </p>
                <div className='mt-8 flex flex-wrap items-center gap-4'>
                  <Button variant='primary' onClick={handleCreateWorkflow} className='inline-flex items-center justify-center gap-2.5 px-8 py-3.5 text-base font-semibold rounded-full shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:scale-105 transition-all duration-200'>
                    <PlusIcon className='h-5 w-5'/>
                    {t('Create Workflow')}
                  </Button>
                  <button onClick={() => setActiveTab('templates')} className='inline-flex items-center justify-center gap-2.5 px-8 py-3.5 text-base font-semibold rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-200'>
                    <TemplateIcon className='h-5 w-5'/>
                    {t('Browse Templates')}
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} className='inline-flex items-center justify-center gap-2.5 px-8 py-3.5 text-base font-semibold rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 hover:border-white/30 hover:scale-105 transition-all duration-200'>
                    <UploadIcon className='h-5 w-5'/>
                    {t('Import Workflow')}
                  </button>
                  <input ref={fileInputRef} type='file' accept='.json' onChange={handleImportWorkflow} className='hidden'/>
                </div>
              </div>
              {/* How to Start link - top right */}
              <button onClick={() => setShowVideoModal(true)} className='absolute top-4 right-4 sm:top-6 sm:right-6 inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-white/90 hover:text-white transition-colors'>
                <VideoCameraIcon className='h-4 w-4'/>
                <span>{t('How to Start')}</span>
              </button>
            </div>
          </div>

          {/* Search and Filter Bar */}
          <div className='mt-10 space-y-6'>
            <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
              {/* Tabs */}
              <nav className='flex flex-wrap gap-2'>
                <button onClick={() => setActiveTab('my-workflows')} className={clsxm('inline-flex items-center gap-2.5 rounded-xl px-5 py-2.5 text-sm font-semibold ring-1 transition-all duration-200', activeTab === 'my-workflows'
            ? 'bg-slate-900 text-white ring-slate-900 shadow-lg dark:bg-white dark:text-slate-900 dark:ring-white'
            : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 dark:bg-slate-900 dark:text-slate-400 dark:ring-white/10 dark:hover:bg-slate-800 dark:hover:text-slate-100')}>
                  <FolderIcon className='h-4 w-4'/>
                  {t('My Workflows')}
                  <span className={clsxm('rounded-full px-2 py-0.5 text-xs font-bold', activeTab === 'my-workflows'
            ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')}>
                    {filteredWorkflows.length}
                  </span>
                </button>
                {sharedWorkflowsList.length > 0 && (<button onClick={() => setActiveTab('shared')} className={clsxm('inline-flex items-center gap-2.5 rounded-xl px-5 py-2.5 text-sm font-semibold ring-1 transition-all duration-200', activeTab === 'shared'
                ? 'bg-slate-900 text-white ring-slate-900 shadow-lg dark:bg-white dark:text-slate-900 dark:ring-white'
                : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 dark:bg-slate-900 dark:text-slate-400 dark:ring-white/10 dark:hover:bg-slate-800 dark:hover:text-slate-100')}>
                    <ShareIcon className='h-4 w-4'/>
                    {t('Shared with me')}
                    <span className={clsxm('rounded-full px-2 py-0.5 text-xs font-bold', activeTab === 'shared'
                ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')}>
                      {sharedWorkflowsList.length}
                    </span>
                  </button>)}
                <button onClick={() => setActiveTab('templates')} className={clsxm('inline-flex items-center gap-2.5 rounded-xl px-5 py-2.5 text-sm font-semibold ring-1 transition-all duration-200', activeTab === 'templates'
            ? 'bg-slate-900 text-white ring-slate-900 shadow-lg dark:bg-white dark:text-slate-900 dark:ring-white'
            : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50 hover:text-slate-900 dark:bg-slate-900 dark:text-slate-400 dark:ring-white/10 dark:hover:bg-slate-800 dark:hover:text-slate-100')}>
                  <TemplateIcon className='h-4 w-4'/>
                  {t('Templates')}
                  <span className={clsxm('rounded-full px-2 py-0.5 text-xs font-bold', activeTab === 'templates'
            ? 'bg-white/20 text-white dark:bg-slate-900/20 dark:text-slate-900'
            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400')}>
                    {workflowTemplates.length}
                  </span>
                </button>
              </nav>

              {/* Search */}
              {(activeTab === 'my-workflows' || activeTab === 'shared') && (<div className='relative w-full sm:w-80'>
                  <div className='pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4'>
                    <SearchIcon className='h-5 w-5 text-slate-400'/>
                  </div>
                  <input type='text' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder={t('Search workflows...')} className='block w-full rounded-xl border-0 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 placeholder-slate-400 shadow-sm ring-1 ring-inset ring-slate-200 transition-all duration-200 focus:ring-2 focus:ring-primary-500 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:ring-white/10 dark:focus:ring-primary-500'/>
                </div>)}
            </div>
          </div>

          {/* Content */}
          {activeTab === 'shared' ? (<div className='mt-8'>
              {loading ? (<LoadingSkeleton />) : sharedWorkflowsList.length === 0 ? (<div className='text-center py-20 rounded-3xl border border-slate-200/60 dark:border-white/5 bg-gradient-to-br from-white via-slate-50/30 to-white dark:from-slate-900 dark:via-slate-800/30 dark:to-slate-900'>
                  <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100 ring-1 ring-inset ring-slate-900/5 dark:bg-slate-800 dark:ring-white/10'>
                    <ShareIcon className='h-10 w-10 text-slate-400'/>
                  </div>
                  <h3 className='mt-6 text-xl font-bold text-slate-900 dark:text-slate-100'>
                    {searchQuery
                    ? t('No workflows found')
                    : t('No shared workflows')}
                  </h3>
                  <p className='mt-2 text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto'>
                    {searchQuery
                    ? t('Try adjusting your search or filters')
                    : t('Workflows shared with you by collaborators will appear here')}
                  </p>
                </div>) : (<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                  {sharedWorkflowsList.map((workflow) => (<WorkflowCard key={workflow.id} workflow={workflow} isStarred={starredWorkflows.has(workflow.id)}/>))}
                </div>)}
            </div>) : activeTab === 'my-workflows' ? (<div className='mt-8'>
              {loading ? (<LoadingSkeleton />) : filteredWorkflows.length === 0 ? (<div className='text-center py-20 rounded-3xl border border-slate-200/60 dark:border-white/5 bg-gradient-to-br from-white via-slate-50/30 to-white dark:from-slate-900 dark:via-slate-800/30 dark:to-slate-900'>
                  <div className='mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100 ring-1 ring-inset ring-slate-900/5 dark:bg-slate-800 dark:ring-white/10'>
                    <FolderIcon className='h-10 w-10 text-slate-400'/>
                  </div>
                  <h3 className='mt-6 text-xl font-bold text-slate-900 dark:text-slate-100'>
                    {searchQuery
                    ? t('No workflows found')
                    : t('No workflows yet')}
                  </h3>
                  <p className='mt-2 text-base text-slate-600 dark:text-slate-400 max-w-md mx-auto'>
                    {searchQuery
                    ? t('Try adjusting your search or filters')
                    : t('Get started by creating a new workflow or using a template')}
                  </p>
                  {!searchQuery && (<div className='mt-8 flex flex-col sm:flex-row gap-3 justify-center px-4'>
                      <Button variant='primary' onClick={handleCreateWorkflow} className='inline-flex items-center gap-2'>
                        <PlusIcon className='h-5 w-5'/>
                        {t('Create Workflow')}
                      </Button>
                      <Button variant='outline' onClick={() => setActiveTab('templates')} className='inline-flex items-center gap-2'>
                        <TemplateIcon className='h-5 w-5'/>
                        {t('Browse Templates')}
                      </Button>
                    </div>)}
                </div>) : (<>
                  {/* Starred Workflows Section */}
                  {starredWorkflowsList.length > 0 && (<div className='mb-10'>
                      <div className='flex items-center gap-2 mb-5'>
                        <StarIconSolid className='h-5 w-5 text-yellow-500'/>
                        <h2 className='text-lg font-bold text-slate-900 dark:text-slate-100'>
                          {t('Favorites')}
                        </h2>
                      </div>
                      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                        {starredWorkflowsList.map((workflow) => (<WorkflowCard key={workflow.id} workflow={workflow} isStarred={true}/>))}
                      </div>
                    </div>)}

                  {/* All Workflows Section */}
                  <div>
                    {starredWorkflowsList.length > 0 && (<div className='flex items-center gap-2 mb-5'>
                        <FolderIcon className='h-5 w-5 text-slate-600 dark:text-slate-400'/>
                        <h2 className='text-lg font-bold text-slate-900 dark:text-slate-100'>
                          {t('All Workflows')}
                        </h2>
                      </div>)}
                    <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                      {filteredWorkflows.map((workflow) => (<WorkflowCard key={workflow.id} workflow={workflow} isStarred={starredWorkflows.has(workflow.id)}/>))}
                    </div>
                  </div>
                </>)}
            </div>) : (<div className='mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {workflowTemplates.map((template) => (<div key={template.id} className='group relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-900/5 dark:ring-white/10 transition-all duration-300 hover:shadow-2xl hover:shadow-slate-900/10 hover:-translate-y-1 dark:hover:shadow-black/30 cursor-pointer' onClick={() => router.push(`/workflows/templates/${template.id}`)}>
                  {/* Preview Area */}
                  <div className='relative aspect-[16/9] overflow-hidden'>
                    {template.backgroundImage &&
                    /\.(mp4|webm|mov|ogg)$/i.test(template.backgroundImage) ? (<video src={template.backgroundImage} className='absolute inset-0 h-full w-full object-cover' autoPlay loop muted playsInline preload='metadata'/>) : (<Image src={template.backgroundImage ||
                        'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/15155d33-5936-4025-a26e-fe0585937031.png'} alt='Template preview' fill className='object-cover'/>)}
                    <div className='absolute inset-0 bg-gradient-to-br from-slate-900/40 via-slate-900/30 to-slate-900/40'/>
                    <div className='absolute inset-0 flex items-center justify-center'></div>
                    {/* Gradient overlay on hover */}
                    <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'/>
                  </div>

                  {/* Content */}
                  <div className='p-6'>
                    <h3 className='font-bold text-lg text-slate-900 dark:text-slate-100 mb-3 line-clamp-1'>
                      {template.name}
                    </h3>

                    <p className='text-sm text-slate-600 dark:text-slate-400 line-clamp-3 mb-4'>
                      {template.description}
                    </p>

                    {/* <Button
                  variant='outline'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUseTemplate(template.id);
                  }}
                  className='rounded-full group-hover:bg-primary-600 group-hover:text-white group-hover:border-primary-600 transition-all duration-200'
                >
                  <PlusIcon className='h-4 w-4 mr-2' />
                  {t('Use Template')}
                </Button> */}
                  </div>
                </div>))}
            </div>)}
        </div>
      </main>

      {/* Video Tutorial Modal */}
      {showVideoModal && (<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm' onClick={() => setShowVideoModal(false)}>
          <div className='relative w-full max-w-4xl mx-4' onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowVideoModal(false)} className='absolute -top-12 right-0 p-2 text-white/80 hover:text-white transition-colors' aria-label={t('Close')}>
              <XIcon className='h-8 w-8'/>
            </button>
            <div className='relative aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl'>
              <iframe src='https://www.youtube.com/embed/t4I5dQ3p2f8?autoplay=1' title='How to Start with AI Workflows' allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture' allowFullScreen className='absolute inset-0 w-full h-full'/>
            </div>
          </div>
        </div>)}
    </DashboardLayout>);
}

    async function __Next_Translate__getStaticProps__1a0cbfb658c__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/workflows/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a0cbfb658c__ as getStaticProps }
  